<template>
  <div
    class="customizer d-none d-md-block"
    :class="{ open: isCustomizerOpen }"
  >
    <!-- Toggler -->
    <b-link
      class="customizer-toggle d-flex align-items-center justify-content-center"
      @click="toggleCustomizer()"
    >
      <feather-icon icon="SettingsIcon" size="15" class="spinner" />
    </b-link>

    <!-- Header -->
    <div class="customizer-section d-flex justify-content-between align-items-center">
      <div>
        <h4 class="mb-0">System Settings</h4>
        <small class="text-muted">Manage sensitive operation controls</small>
      </div>
      <feather-icon
        icon="XIcon"
        size="18"
        class="cursor-pointer text-muted"
        @click="toggleCustomizer()"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="customizer-loading d-flex flex-column align-items-center justify-content-center">
      <b-spinner variant="primary" style="width:2rem;height:2rem;" />
      <small class="text-muted mt-1">Loading settings…</small>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && configurations.length === 0" class="customizer-empty d-flex flex-column align-items-center justify-content-center text-center px-2">
      <feather-icon icon="SlidersIcon" size="36" class="text-muted mb-1" />
      <p class="text-muted mb-0">No system configurations found.</p>
    </div>

    <!-- Content -->
    <vue-perfect-scrollbar
      v-else
      :settings="perfectScrollbarSettings"
      class="ps-customizer-area scroll-area"
    >
      <validation-observer ref="dataForm" #default="{ invalid }">
        <b-form ref="form" @submit.stop.prevent="">

          <div
            v-for="(configuration, i) in configurations"
            :key="configuration.id"
            class="customizer-section pt-1"
          >
            <!-- Section Header -->
            <div class="section-header mb-75">
              <h6 class="mb-0 font-weight-bold">{{ configuration.name }}</h6>
              <small v-if="configuration.description" class="text-muted font-italic">
                {{ configuration.description }}
              </small>
            </div>

            <!-- Top-level preferences (rare, kept for completeness) -->
            <b-row v-if="configuration.preferences.length > 0" class="align-items-end mb-50">
              <b-col
                v-for="(preference, x) in configuration.preferences"
                :key="preference.id"
                :cols="Math.floor(10 / configuration.preferences.length)"
              >
                <b-form-group
                  :label="preference.key"
                  :label-for="`pref-${preference.id}`"
                  class="mb-0"
                  v-if="preference.type === null"
                >
                  <validation-provider #default="{ errors }" :name="preference.key" rules="">
                    <b-form-input
                      :id="`pref-${preference.id}`"
                      v-model="configurations[i].preferences[x].value"
                      :state="errors.length > 0 ? false : null"
                      size="sm"
                      number
                      :disabled="!preference.updating"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="serverErrors && serverErrors[preference.key.toLowerCase()]"
                      class="text-danger"
                    >{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group
                  :label="preference.key"
                  :label-for="`pref-${preference.id}`"
                  class="mb-0"
                  v-else
                >
                  <validation-provider #default="{ errors }" :name="preference.key" rules="">
                    <b-input-group>
                      <b-form-input
                        :id="`pref-${preference.id}`"
                        v-model="configurations[i].preferences[x].value"
                        :state="errors.length > 0 ? false : null"
                        size="sm"
                        number
                        :disabled="!preference.updating"
                      />
                      <b-input-group-append>
                      <b-dropdown
                        text="Action"
                        variant="outline-primary"
                        right
                        size="sm"
                        :disabled="!preference.updating"
                      >
                        <b-dropdown-item>Action </b-dropdown-item>
                        <b-dropdown-item>Another Action</b-dropdown-item>
                        <b-dropdown-item>Something else here</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item>Separated link</b-dropdown-item>
                      </b-dropdown>
                    </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="serverErrors && serverErrors[preference.key.toLowerCase()]"
                      class="text-danger"
                    >{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="2" class="d-flex align-items-end pb-25">
                <b-button
                  v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon"
                  @click="toggleUpdating(i, -1)"
                >
                  <feather-icon icon="Edit3Icon" />
                </b-button>
              </b-col>
            </b-row>

            <!-- Components -->
            <div v-if="configuration.components.length > 0">
              <div
                v-for="(component, m) in configuration.components"
                :key="component.id"
                class="component-block mb-75 p-1 rounded"
              >
                <div class="d-flex align-items-center justify-content-between mb-75">
                  <span class="component-label text-uppercase font-weight-bold">
                    {{ component.name }}
                  </span>

                  <!-- Status pill -->
                  <b-badge
                    v-if="component.preferences.some(p => p.saving)"
                    variant="light-warning"
                    pill
                  >
                    <b-spinner small style="width:.7rem;height:.7rem;" class="mr-25" />
                    Saving…
                  </b-badge>
                  <b-badge
                    v-else-if="component.preferences.some(p => p.needSaving)"
                    variant="light-info"
                    pill
                  >
                    Unsaved changes
                  </b-badge>
                  <b-badge
                    v-else-if="component.preferences.some(p => p.updating)"
                    variant="light-primary"
                    pill
                  >
                    Editing
                  </b-badge>
                </div>

                <b-row class="align-items-end">
                  <b-col
                    v-for="(preference, n) in component.preferences"
                    :key="preference.id"
                    :cols="Math.floor(10 / component.preferences.length)"
                  >
                    <b-form-group
                      :label="preference.key + ' 0'"
                      :label-for="`comp-pref-${preference.id}`"
                      class="mb-0"
                      v-if="preference.type === null"
                    >
                      <validation-provider #default="{ errors }" :name="preference.key" rules="">
                        <b-form-input
                          :id="`comp-pref-${preference.id}`"
                          v-model="configurations[i].components[m].preferences[n].value"
                          :state="errors.length > 0 ? false : null"
                          size="sm"
                          number
                          :disabled="!preference.updating"
                          :class="{ 'border-warning': preference.needSaving }"
                          @change="checkSaving(i, m)"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                        <small
                          v-if="serverErrors && serverErrors[preference.key.toLowerCase()]"
                          class="text-danger"
                        >{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                      </validation-provider>
                    </b-form-group>

                    <b-form-group
                      :label="preference.key"
                      :label-for="`comp-pref-${preference.id}`"
                      class="mb-0"
                      v-else
                    >
                      <validation-provider #default="{ errors }" :name="preference.key" rules="">
                        <b-input-group>
                          <b-form-input
                            :id="`comp-pref-${preference.id}`"
                            v-model="configurations[i].components[m].preferences[n].value"
                            :state="errors.length > 0 ? false : null"
                            size="sm"
                            number
                            :disabled="!preference.updating"
                            :class="{ 'border-warning': preference.needSaving }"
                            @change="checkSaving(i, m)"
                          />
                          <b-input-group-append>
                            <b-dropdown
                              text="Action"
                              variant="outline-primary"
                              right
                              size="sm"
                            >
                              <b-dropdown-item>Actions</b-dropdown-item>
                              <b-dropdown-item>Another Action</b-dropdown-item>
                              <b-dropdown-item>Something else here</b-dropdown-item>
                              <b-dropdown-divider />
                              <b-dropdown-item>Separated link</b-dropdown-item>
                            </b-dropdown>
                          </b-input-group-append>
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                        <small
                          v-if="serverErrors && serverErrors[preference.key.toLowerCase()]"
                          class="text-danger"
                        >{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Action button -->
                  <b-col cols="2" class="d-flex align-items-end pb-25">
                    <b-button
                      v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                      :variant="getButtonVariant(i, m)"
                      size="sm"
                      class="btn-icon"
                      :disabled="component.preferences.some(p => p.saving)"
                      @click="toggleUpdating(i, m)"
                    >
                      <!-- Saving spinner -->
                      <b-spinner
                        v-if="component.preferences.some(p => p.saving)"
                        small
                        style="width:.85rem;height:.85rem;"
                      />
                      <!-- Save icon (has unsaved changes) -->
                      <feather-icon
                        v-else-if="component.preferences.some(p => p.needSaving)"
                        icon="SaveIcon"
                      />
                      <!-- Confirm / apply icon (editing but no changes yet) -->
                      <feather-icon
                        v-else-if="component.preferences.some(p => p.updating)"
                        icon="CheckIcon"
                      />
                      <!-- Edit icon (idle) -->
                      <feather-icon
                        v-else
                        icon="Edit3Icon"
                      />
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>

        </b-form>
      </validation-observer>
    </vue-perfect-scrollbar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow, BCol,
  BForm, BFormGroup, BFormInput, BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BLink, BButton, BSpinner, BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import useAppCustomizer from './useAppCustomizer'
import customizersStoreModule from './customizerStoreModule'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    BRow, BCol,
    BForm, BFormGroup, BFormInput, BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BLink, BButton, BSpinner, BBadge,
    ValidationObserver, ValidationProvider,
    VuePerfectScrollbar,
  },

  directives: { Ripple },

  setup() {
    const loading    = ref(false)
    const dataForm   = ref(null)
    const serverErrors = ref(null)
    const configurations = ref([])

    // ── Store ────────────────────────────────────────────────────────────────
    const STORE_KEY = 'cromis-customizer'
    if (!store.hasModule(STORE_KEY)) store.registerModule(STORE_KEY, customizersStoreModule)
    onUnmounted(() => { if (store.hasModule(STORE_KEY)) store.unregisterModule(STORE_KEY) })

    // ── App-config composable ────────────────────────────────────────────────
    const {
      isVerticalMenuCollapsed,
      isCustomizerOpen,
      skin, skinOptions,
      contentWidth, contentWidthOptions,
      isRTL,
      routerTransition, routerTransitionOptions,
      layoutType, layoutTypeOptions,
      isNavMenuHidden,
      navbarColors, navbarTypes, navbarBackgroundColor, navbarType,
      footerTypes, footerType,
    } = useAppCustomizer()

    if (layoutType.value === 'horizontal') {
      const i1 = skinOptions.findIndex(s => s.value === 'semi-dark')
      delete skinOptions[i1]
      const i2 = navbarTypes.findIndex(t => t.value === 'hidden')
      delete navbarTypes[i2]
    }

    const perfectScrollbarSettings = { maxScrollbarLength: 60, wheelPropagation: false }

    // ── Helpers ──────────────────────────────────────────────────────────────
    const getButtonVariant = (i, m) => {
      const prefs = configurations.value[i].components[m].preferences
      if (prefs.some(p => p.saving))     return 'flat-warning'
      if (prefs.some(p => p.needSaving)) return 'flat-success'
      if (prefs.some(p => p.updating))   return 'flat-primary'
      return 'flat-secondary'
    }

    // ── Data fetching ────────────────────────────────────────────────────────
    const mapPreference = (pref, parentId, type) => ({
      id: pref.id,
      preference_id: parentId,
      preference_type: type,
      key: pref.key,
      previousValue: pref.value,
      value: pref.value,
      updating: false,
      needSaving: false,
      saving: false,
    })

    const fetchSettings = async () => {
      try {
        const { data } = await store.dispatch(`${STORE_KEY}/list`)
        configurations.value = data.settings.map(setting => ({
          id: setting.id,
          name: setting.name,
          description: setting.description ?? null,
          preferences: setting.preferences.map(p => mapPreference(p, setting.id, 0)),
          components: setting.components.map(comp => ({
            id: comp.id,
            name: comp.name,
            preferences: comp.preferences.map(p => mapPreference(p, comp.id, 1)),
          })),
        }))
      } catch (err) {
        console.error('[Customizer] fetchSettings error:', err)
      }
    }

    // ── Toggle open / close ──────────────────────────────────────────────────
    const toggleCustomizer = async () => {
      isCustomizerOpen.value = !isCustomizerOpen.value
      if (isCustomizerOpen.value) {
        loading.value = true
        await fetchSettings()
        loading.value = false
      }
    }

    // ── Edit / save toggle ───────────────────────────────────────────────────
    const toggleUpdating = async (i, m) => {
      const isTopLevel = m < 0
      const prefs = isTopLevel
        ? configurations.value[i].preferences
        : configurations.value[i].components[m].preferences

      const hasUnsaved = prefs.some(p => p.needSaving)

      // Nothing changed yet → just flip the editing flag
      if (!hasUnsaved) {
        prefs.forEach(p => { p.updating = !p.updating })
        return
      }

      // Mark as saving
      const changed = prefs.filter(p => p.previousValue !== p.value)
      changed.forEach(p => { p.saving = true })

      try {
        await store.dispatch(`${STORE_KEY}/updatePreferences`, { preferences: changed })
        await fetchSettings()
      } catch (err) {
        console.error('[Customizer] save error:', err)
        changed.forEach(p => { p.saving = false })
      }
    }

    // ── Track unsaved changes per field ──────────────────────────────────────
    const checkSaving = (i, m) => {
      configurations.value[i].components[m].preferences.forEach(p => {
        p.needSaving = p.value !== p.previousValue
      })
    }

    return {
      // State
      loading, dataForm, serverErrors, configurations,
      perfectScrollbarSettings,
      // Customizer
      isCustomizerOpen, toggleCustomizer,
      // Layout
      isVerticalMenuCollapsed,
      skin, skinOptions,
      contentWidth, contentWidthOptions,
      isRTL,
      routerTransition, routerTransitionOptions,
      layoutType, layoutTypeOptions,
      isNavMenuHidden,
      navbarColors, navbarTypes, navbarBackgroundColor, navbarType,
      footerTypes, footerType,
      // Methods
      toggleUpdating, checkSaving, getButtonVariant,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.customizer-section {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;

  .dark-layout & { border-color: $theme-dark-border-color; }

  .form-group {
    margin-bottom: 1rem;
    &:last-of-type { margin-bottom: 0; }
    ::v-deep legend { font-weight: 500; }
  }
}

.section-header {
  border-left: 3px solid var(--primary);
  padding-left: .6rem;
}

.component-block {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid #ebe9f1;
  transition: border-color .2s;

  .dark-layout & {
    background: rgba(255, 255, 255, 0.02);
    border-color: $theme-dark-border-color;
  }

  &:hover { border-color: var(--primary); }
}

.component-label {
  font-size: 0.7rem;
  letter-spacing: .06em;
  color: var(--secondary);
}

.customizer-loading,
.customizer-empty {
  height: calc(100% - 83px);
  padding: 2rem;
}

.ps-customizer-area {
  height: calc(100% - 83px);
}

.mark-active {
  box-shadow: 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
}
</style>